import React, { useState } from 'react';
import { BadgeCheckIcon, TrendingUpIcon, UploadIcon } from 'lucide-react';
import { OnboardingDone, OnboardingFrame, type Step } from '../../components/onboarding/OnboardingFrame';
import { Card, KeyValue } from '../../components/ui/Panels';
import { ButtonLink } from '../../components/ui/Button';
import { Field, Input, Select, Switch, Textarea } from '../../components/ui/Field';
import { cuisines, dietaryCapabilities, equipmentCatalog } from '../../data/taxonomy';
import { useLocale } from '../../contexts/LocaleContext';
import { currency } from '../../utils/format';
import { SERVICE_FEE_RATE } from '../../utils/pricing';
import { cn } from '../../utils/cn';
const steps: Step[] = [{
  id: 'about',
  label: 'About you',
  title: 'Introduce yourself to guests',
  blurb: 'Guests scan the portrait, then the headline, then the price. All three do work — the bio can wait.'
}, {
  id: 'menu',
  label: 'First menu',
  title: 'Publish one menu, priced per guest',
  blurb: 'We do not list hourly rates. A price per guest, a minimum head count and the hours included is what makes you comparable — and what stops anyone undercutting you with a hidden hourly.'
}, {
  id: 'rules',
  label: 'Booking rules',
  title: 'What is worth your evening?',
  blurb: 'Your minimum order is enforced before a request can reach you, so you never negotiate down from a bad brief.'
}, {
  id: 'area',
  label: 'Service area',
  title: 'How far will you travel?',
  blurb: 'Requests from outside your area never reach your inbox. Traffic, not distance, is what costs you the evening.'
}, {
  id: 'checks',
  label: 'Verification',
  title: 'Get your badges',
  blurb: 'Guests can filter search down to fully verified chefs. Identity is instant; food hygiene is checked by a person within two working days.'
}];
export function ChefOnboarding() {
  const {
    locale,
    currencyCode
  } = useLocale();
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(false);
  const [name, setName] = useState('');
  const [headline, setHeadline] = useState('');
  const [bio, setBio] = useState('');
  const [cuisineIds, setCuisineIds] = useState<string[]>([]);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [menuName, setMenuName] = useState('');
  const [pricePerGuest, setPricePerGuest] = useState(1200);
  const [minGuests, setMinGuests] = useState(2);
  const [includedHours, setIncludedHours] = useState(3);
  const [groceries, setGroceries] = useState('included');
  const [needs, setNeeds] = useState<string[]>([]);
  const [delivers, setDelivers] = useState(false);
  const [minimumSpend, setMinimumSpend] = useState(3600);
  const [deposit, setDeposit] = useState(30);
  const [cancellation, setCancellation] = useState('moderate');
  const [instant, setInstant] = useState(false);
  const [base, setBase] = useState('Ari');
  const [radius, setRadius] = useState(8);
  const [travelPerKm, setTravelPerKm] = useState(20);
  const [idUploaded, setIdUploaded] = useState(false);
  const [hygieneUploaded, setHygieneUploaded] = useState(false);
  const [insurance, setInsurance] = useState(false);
  const toggle = (list: string[], set: (v: string[]) => void, id: string) => set(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  const smallest = pricePerGuest * minGuests;
  const takeHome = Math.round(smallest * (1 - SERVICE_FEE_RATE));
  const canAdvance = index === 0 ? name.trim().length > 2 && headline.trim().length > 8 && cuisineIds.length > 0 : index === 1 ? menuName.trim().length > 3 && pricePerGuest > 0 : index === 4 ? idUploaded : true;
  if (done) {
    return <OnboardingDone title="Your listing is in review" body="Identity is confirmed. Once your food hygiene certificate clears — usually two working days — you appear in search with the verified badge and can start taking requests." primary={<ButtonLink to="/chef" variant="action" size="lg" data-id="element-1951" data-mp="1w1lkcs662dn3">
            Go to my dashboard
          </ButtonLink>} secondary={<ButtonLink to="/chef/studio/menus" variant="outline" size="lg" data-id="element-1952" data-mp="25t7vkoii8cpu">
            Add another menu
          </ButtonLink>} data-id="element-1950" data-mp="7jsh260ocgjm" />;
  }
  const aside = index === 1 || index === 2 ? <Card className="p-5" data-id="element-1953" data-mp="1d5vjx9dvtpbb">
        <div className="flex items-center gap-2" data-id="element-1954" data-mp="1d6n4lp5xy2mt">
          <TrendingUpIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-1955" data-mp="1i3dkabdu2w7p" />
          <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1956" data-mp="1n2zxwlxkqeh1">What this earns</h2>
        </div>
        <p className="tnum mt-3 font-display text-2xl font-semibold text-primary" data-id="element-1957" data-mp="1s2cu3xac4xwa">
          {currency(takeHome, locale, currencyCode)}
        </p>
        <p className="text-[13px] text-ink-2" data-id="element-1958" data-mp="2yhvhmmb2v64g">take-home on your smallest booking</p>
        <dl className="mt-4" data-id="element-1959" data-mp="33forbpblai9h">
          <KeyValue label="Guests pay" value={currency(smallest, locale, currencyCode)} data-id="element-1960" data-mp="33j2u2yo60thg" />
          <KeyValue label="Service fee" value={`− ${currency(smallest - takeHome, locale, currencyCode)}`} data-id="element-1961" data-mp="rvz7xcwhvgkw" />
          <KeyValue label="Search shows" value={`from ${currency(pricePerGuest, locale, currencyCode)} / guest`} data-id="element-1962" data-mp="mx5xjtci85oy" />
        </dl>
      </Card> : index === 4 ? <Card className="p-5" data-id="element-1963" data-mp="d4gyxz21ymb1">
        <div className="flex items-center gap-2" data-id="element-1964" data-mp="d35udx0s9aiv">
          <BadgeCheckIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-1965" data-mp="86ylbvd3biwp" />
          <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1966" data-mp="43sikxp6s9uz">Why it matters</h2>
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-2" data-id="element-1967" data-mp="3vbobuct1ghh0">
          Verified chefs get roughly twice the request volume, because “fully verified only” is the most-used filter on
          the search page.
        </p>
      </Card> : <Card className="p-5" data-id="element-1968" data-mp="1tgvy32nzpwrz">
        <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1969" data-mp="1ojvucyem5641">You keep control</h2>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-2" data-id="element-1970" data-mp="3izcfwve6a9l9">
          You set your menus, prices, minimums, areas and cancellation terms. We take 10% of what the guest pays, and
          nothing else.
        </p>
      </Card>;
  return <OnboardingFrame steps={steps} index={index} onBack={() => setIndex(i => Math.max(0, i - 1))} onNext={() => index === steps.length - 1 ? setDone(true) : setIndex(i => i + 1)} nextLabel={index === steps.length - 1 ? 'Submit for review' : 'Continue'} nextDisabled={!canAdvance} exitTo="/for-chefs" aside={aside} data-id="element-1971" data-mp="390d2z0mdjrsg">
      {index === 0 && <div className="max-w-xl space-y-4" data-id="element-1972" data-mp="3rv1twjdak1om">
          <Field label="Your name, as guests will see it" id="co-name" data-id="element-1973" data-mp="lcyo29rty0x">
            <Input id="co-name" value={name} onChange={e => setName(e.target.value)} placeholder="Ploy Sirichai" data-id="element-1974" data-mp="2gdscs3hmxlqh" />
          </Field>
          <Field label="Headline" id="co-headline" hint="one line under your name in search" data-id="element-1975" data-mp="2lder6g97980v">
            <Input id="co-headline" value={headline} onChange={e => setHeadline(e.target.value)} maxLength={90} placeholder="Southern Thai curries and a proper som tam, cooked in your kitchen" data-id="element-1976" data-mp="13dxm5mpdu2a2" />
          </Field>
          <Field label="About you" id="co-bio" hint="optional for now" data-id="element-1977" data-mp="18f4txlriz1gq">
            <Textarea id="co-bio" rows={4} value={bio} onChange={e => setBio(e.target.value)} data-id="element-1978" data-mp="ko91qnj5q48y" />
          </Field>

          <div data-id="element-1979" data-mp="fq7b9hjej2ng">
            <p className="mb-2 text-[13px] font-semibold text-ink" data-id="element-1980" data-mp="fqpe4x0kkf4c">Cuisines you cook</p>
            <div className="flex flex-wrap gap-1.5" data-id="element-1981" data-mp="thuiudisnxlj">
              {cuisines.map(cuisine => {
            const on = cuisineIds.includes(cuisine.id);
            return <button key={cuisine.id} type="button" aria-pressed={on} onClick={() => toggle(cuisineIds, setCuisineIds, cuisine.id)} className={cn('rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out', on ? 'border-primary bg-primary-soft text-primary' : 'border-hairline text-ink-2 hover:text-ink')} data-id="element-1982" data-mp="10043xyuh622">
                    {cuisine.label}
                  </button>;
          })}
            </div>
          </div>

          <div data-id="element-1983" data-mp="14qqop2gmdv7c">
            <p className="mb-1 text-[13px] font-semibold text-ink" data-id="element-1984" data-mp="19reifvw8gq59">Diets you can genuinely handle</p>
            <p className="mb-2 text-[12px] text-ink-3" data-id="element-1985" data-mp="1eims8t1h6diy">Guests filter on these. Allergies are not a place to stretch.</p>
            <div className="flex flex-wrap gap-1.5" data-id="element-1986" data-mp="1ik81m4esw9oe">
              {dietaryCapabilities.map(cap => {
            const on = capabilities.includes(cap);
            return <button key={cap} type="button" aria-pressed={on} onClick={() => toggle(capabilities, setCapabilities, cap)} className={cn('rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out', on ? 'border-primary bg-primary-soft text-primary' : 'border-hairline text-ink-2 hover:text-ink')} data-id="element-1987" data-mp="1shmfbw9cakd0">
                    {cap}
                  </button>;
          })}
            </div>
          </div>
        </div>}

      {index === 1 && <div className="max-w-xl space-y-4" data-id="element-1988" data-mp="2bmis1g924acp">
          <Field label="Menu name" id="co-menu" data-id="element-1989" data-mp="2bl7s5riqfmqv">
            <Input id="co-menu" value={menuName} onChange={e => setMenuName(e.target.value)} placeholder="Weeknight Thai table" data-id="element-1990" data-mp="27lfckuux8gc0" />
          </Field>

          <div className="grid grid-cols-2 gap-4" data-id="element-1991" data-mp="2zc3ck3kuzfo3">
            <Field label="Price per guest" id="co-price" data-id="element-1992" data-mp="2za8vx1gme9pt">
              <Input id="co-price" type="number" min={100} step={50} value={pricePerGuest} onChange={e => setPricePerGuest(Number(e.target.value) || 0)} className="tnum" data-id="element-1993" data-mp="sne63xotup44" />
            </Field>
            <Field label="Minimum guests" id="co-min" data-id="element-1994" data-mp="wmem78fx7dnf">
              <Input id="co-min" type="number" min={1} max={30} value={minGuests} onChange={e => setMinGuests(Number(e.target.value) || 1)} className="tnum" data-id="element-1995" data-mp="1bkozj1ns2obn" />
            </Field>
            <Field label="Hours included" id="co-hours" data-id="element-1996" data-mp="1gbowu80t78eo">
              <Input id="co-hours" type="number" min={0} step={0.5} value={includedHours} onChange={e => setIncludedHours(Number(e.target.value) || 0)} className="tnum" data-id="element-1997" data-mp="1geb5kazfrtyh" />
            </Field>
            <Field label="Groceries" id="co-groc" data-id="element-1998" data-mp="2dx8bhjyfso1r">
              <Select id="co-groc" value={groceries} onChange={e => setGroceries(e.target.value)} data-id="element-1999" data-mp="2dwzyzt687kr3">
                <option value="included" data-id="element-2000" data-mp="1yzpmqhlreaub">Included in the price</option>
                <option value="at_cost" data-id="element-2001" data-mp="23yj10c8ve5vj">At cost, with receipts</option>
                <option value="estimate" data-id="element-2002" data-mp="1q879ofk40b5g">Estimated, trued up</option>
              </Select>
            </Field>
          </div>

          <div className="border-y border-hairline" data-id="element-2003" data-mp="1uyw3w2n6bm8a">
            <Switch checked={delivers} onChange={setDelivers} label="Offer this as delivery too" description="Cooked in your kitchen and delivered, priced per portion." data-id="element-2004" data-mp="1v0gwvntuax41" />
          </div>

          <div data-id="element-2005" data-mp="17a4ibu43a24h">
            <p className="mb-2 text-[13px] font-semibold text-ink" data-id="element-2006" data-mp="179l7sygrqg0h">Equipment this menu needs</p>
            <p className="mb-2 text-[12px] text-ink-3" data-id="element-2007" data-mp="1ba5645d2vpo7">
              We check these against the guest’s kitchen when you quote and warn you what to bring.
            </p>
            <div className="flex flex-wrap gap-1.5" data-id="element-2008" data-mp="396bttjo55r5s">
              {equipmentCatalog.map(item => {
            const on = needs.includes(item.id);
            return <button key={item.id} type="button" aria-pressed={on} onClick={() => toggle(needs, setNeeds, item.id)} className={cn('rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors duration-150 ease-out', on ? 'border-primary bg-primary-soft text-primary' : 'border-hairline text-ink-3 hover:text-ink')} data-id="element-2009" data-mp="2z8xsysepbayl">
                    {item.label}
                  </button>;
          })}
            </div>
          </div>
        </div>}

      {index === 2 && <div className="max-w-xl space-y-4" data-id="element-2010" data-mp="mzh6ybm3p93x">
          <Field label="Minimum order" id="co-minspend" hint="requests below this are blocked automatically" data-id="element-2011" data-mp="n0s966zwe8sx">
            <Input id="co-minspend" type="number" min={0} step={100} value={minimumSpend} onChange={e => setMinimumSpend(Number(e.target.value) || 0)} className="tnum" data-id="element-2012" data-mp="r12erijs2x79" />
          </Field>
          <div className="grid grid-cols-2 gap-4" data-id="element-2013" data-mp="15pfcol2feq4e">
            <Field label="Deposit" id="co-dep" hint="taken when a guest accepts" data-id="element-2014" data-mp="1aoa2w82q3gyj">
              <Select id="co-dep" value={String(deposit)} onChange={e => setDeposit(Number(e.target.value))} data-id="element-2015" data-mp="1eqmxq1fxlugj">
                {[20, 25, 30, 40, 50].map(p => <option key={p} value={p} data-id="element-2016" data-mp="1jooqje35t857">
                    {p}%
                  </option>)}
              </Select>
            </Field>
            <Field label="Cancellation" id="co-cancel" data-id="element-2017" data-mp="3lg1kx97i3xn0">
              <Select id="co-cancel" value={cancellation} onChange={e => setCancellation(e.target.value)} data-id="element-2018" data-mp="3lio1tzga7phw">
                <option value="flexible" data-id="element-2019" data-mp="2l7lg7hy4i9al">Flexible — full refund to 48 hrs</option>
                <option value="moderate" data-id="element-2020" data-mp="2l9o1xps8tg9i">Moderate — full to 7 days, then 50%</option>
                <option value="strict" data-id="element-2021" data-mp="3042ou1exh9tg">Strict — deposit non-refundable</option>
              </Select>
            </Field>
          </div>

          <div className="border-t border-hairline" data-id="element-2022" data-mp="27g6lweyrmlo8">
            <Switch checked={instant} onChange={setInstant} label="Allow instant book" description="Guests can confirm your published menus without waiting for a quote. You keep the right to decline within 12 hours." data-id="element-2023" data-mp="27gomtowdho2h" />
          </div>

          {minimumSpend > smallest && <div className="rounded-lg border border-accent/50 bg-accent-soft p-3.5" data-id="element-2024" data-mp="1opfe3huff3by">
              <p className="text-[13px] leading-snug text-on-accent" data-id="element-2025" data-mp="1onch2duuw9xq">
                Your minimum of {currency(minimumSpend, locale, currencyCode)} is above what your own menu bills at{' '}
                {minGuests} guests ({currency(smallest, locale, currencyCode)}). Guests would need{' '}
                {Math.ceil(minimumSpend / pricePerGuest)} at the table.
              </p>
            </div>}
        </div>}

      {index === 3 && <div className="max-w-xl space-y-5" data-id="element-2026" data-mp="3qfmis8e80jrt">
          <Field label="Where you are based" id="co-base" data-id="element-2027" data-mp="hz1hzevqswzs">
            <Select id="co-base" value={base} onChange={e => setBase(e.target.value)} data-id="element-2028" data-mp="hzkqk4zi6915">
              {['Ari', 'Phrom Phong', 'Thonglor', 'Sathorn', 'Silom', 'Ratchada', 'On Nut'].map(d => <option key={d} value={d} data-id="element-2029" data-mp="8xtl918vk47t">
                  {d}
                </option>)}
            </Select>
          </Field>

          <div data-id="element-2030" data-mp="2tou3hcz42vr3">
            <label htmlFor="co-radius" className="flex items-baseline justify-between gap-3 text-[13px]" data-id="element-2031" data-mp="2to2g2pfo44bw">
              <span className="font-semibold text-ink" data-id="element-2032" data-mp="2xqfltduf028o">Travel included within</span>
              <span className="tnum text-ink-2" data-id="element-2033" data-mp="147zx7gnadnvg">{radius} km</span>
            </label>
            <input id="co-radius" type="range" min={2} max={25} value={radius} onChange={e => setRadius(Number(e.target.value))} className="mt-2 w-full accent-primary" data-id="element-2034" data-mp="z8dif2rt7zkj" />
            <p className="mt-1 text-[12px] text-ink-3" data-id="element-2035" data-mp="pgpz5p5epou5">
              No travel line appears on quotes inside this ring. Most Bangkok chefs keep it tight.
            </p>
          </div>

          <Field label="Charge per km beyond that" id="co-perkm" data-id="element-2036" data-mp="ggku3lnor2j5">
            <Input id="co-perkm" type="number" min={0} value={travelPerKm} onChange={e => setTravelPerKm(Number(e.target.value) || 0)} className="tnum" data-id="element-2037" data-mp="bjubaqkhxsu1" />
          </Field>

          <div className="rounded-lg bg-oat p-3.5" data-id="element-2038" data-mp="20xz6xef6asfm">
            <p className="text-[13px] leading-relaxed text-ink-2" data-id="element-2039" data-mp="20yinort37as2">
              A guest 14 km away would see a travel line of{' '}
              <span className="tnum font-semibold text-ink" data-id="element-2040" data-mp="2dgbjemk3mna0">
                {currency(Math.max(0, 14 - radius) * travelPerKm, locale, currencyCode)}
              </span>{' '}
              on their quote, itemised separately from the food.
            </p>
          </div>
        </div>}

      {index === 4 && <div className="max-w-xl space-y-3" data-id="element-2041" data-mp="1qgdosr2l7qsm">
          {[{
        key: 'id',
        label: 'Government ID',
        detail: 'Thai ID card or passport. Checked automatically, usually in under a minute.',
        value: idUploaded,
        set: setIdUploaded,
        required: true
      }, {
        key: 'hygiene',
        label: 'Food hygiene certificate',
        detail: 'Reviewed by a person within two working days.',
        value: hygieneUploaded,
        set: setHygieneUploaded,
        required: false
      }].map(doc => <div key={doc.key} className={cn('flex flex-wrap items-center gap-3 rounded-xl border p-4', doc.value ? 'border-primary/40 bg-primary-soft' : 'border-hairline bg-surface')} data-id="element-2042" data-mp="4r3zkadanrs2">
              <span className="min-w-0 flex-1" data-id="element-2043" data-mp="9r0182yir74g">
                <span className="flex items-center gap-2" data-id="element-2044" data-mp="oh7g68pbi80e">
                  <span className="text-sm font-semibold text-ink" data-id="element-2045" data-mp="ofmlmj306p2j">{doc.label}</span>
                  {doc.required && <span className="text-[11px] font-semibold uppercase text-action" data-id="element-2046" data-mp="jkgyxi1s16de">Required</span>}
                </span>
                <span className="mt-0.5 block text-[12px] text-ink-3" data-id="element-2047" data-mp="sj1a7y50cdrl">{doc.detail}</span>
              </span>
              <button type="button" onClick={() => doc.set(!doc.value)} className={cn('inline-flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-2 text-[13px] font-medium transition-colors duration-150 ease-out', doc.value ? 'border-primary bg-primary text-on-primary' : 'border-hairline text-ink-2 hover:text-ink')} data-id="element-2048" data-mp="2z98u1e1tnq2h">
                {doc.value ? <BadgeCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-2049" data-mp="10ztaui91r5px" /> : <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-2050" data-mp="150v9awv53dqu" />}
                {doc.value ? 'Uploaded' : 'Upload'}
              </button>
            </div>)}

          <div className="border-t border-hairline" data-id="element-2051" data-mp="w0p0vpeat3yj">
            <Switch checked={insurance} onChange={setInsurance} label="I carry public liability insurance" description="Adds an insurance badge to your profile once the policy document is verified." data-id="element-2052" data-mp="w2jm74they35" />
          </div>

          <div className="rounded-lg bg-oat p-3.5" data-id="element-2053" data-mp="2cgjkycglchot">
            <p className="text-[13px] leading-relaxed text-ink-2" data-id="element-2054" data-mp="2ggtmncxcolxv">
              You can start taking requests as soon as your ID clears. The hygiene badge appears on your profile later
              without interrupting bookings.
            </p>
          </div>
        </div>}
    </OnboardingFrame>;
}